import { buildVisibleToolPredicate, ERainPlanTool } from '../constants';
import type { IRainAgentConfig } from '../hooks/scope';

import { QUILL_TOOL_ACTIVITIES } from './activities/quill';
import {
  EQuillInternalTool,
  EQuillVisibleTool,
  type IQuillTestRunFocus,
} from './quill';
import { renderConnectionPickerCard } from './renderers/connection';
import {
  renderCreateAssetCard,
  renderCreateCollectionCard,
  renderSetDiscoveredCard,
  renderUpdateCollectionSchemaCard,
} from './renderers/quill-approval';
import {
  createTestRunCardRenderer,
  readBrowserSessionUrl,
  renderAutomationSavedCard,
  renderBrowserSessionCard,
  renderRunCodeCard,
} from './renderers/quill';

/**
 * `show_browser_session` is answered by the client, but unlike the connection
 * picker it asks the user for nothing: the card is the whole point, and the
 * agent should carry on driving the browser while it plays. So the call is
 * acknowledged as soon as its URL has finished streaming.
 */
const BROWSER_SESSION_ACKNOWLEDGEMENT =
  'The live browser view is now visible to the user.';

/**
 * Behavioral config for the Quill agent's chat, consumed by the Rain core hook
 * via the thread adapter.
 *
 * Takes the editor's run-view focus because the "view run" card reflects it and
 * can change it. That state belongs to the editor, so it is closed over here
 * rather than threaded through the runtime.
 */
export const createQuillAgentConfig = (
  testRunFocus: IQuillTestRunFocus,
): IRainAgentConfig => ({
  planMarkStepToolName: ERainPlanTool.PlanMarkStep,
  isVisibleTool: buildVisibleToolPredicate(Object.values(EQuillVisibleTool)),
  clientResolvedTools: new Set<string>([
    EQuillVisibleTool.RequestConnection,
    EQuillVisibleTool.ShowBrowserSession,
  ]),
  internalTools: new Set<string>(Object.values(EQuillInternalTool)),
  toolRenderers: {
    [EQuillVisibleTool.RequestConnection]: renderConnectionPickerCard,
    [EQuillVisibleTool.ShowBrowserSession]: renderBrowserSessionCard,
    [EQuillVisibleTool.SaveAutomation]: renderAutomationSavedCard,
    [EQuillVisibleTool.TestAutomation]: createTestRunCardRenderer(testRunFocus),
    [EQuillVisibleTool.ReplayRun]: createTestRunCardRenderer(testRunFocus),
    [EQuillVisibleTool.RunCode]: renderRunCodeCard,
    [EQuillVisibleTool.CreateCollection]: renderCreateCollectionCard,
    [EQuillVisibleTool.UpdateCollectionSchema]:
      renderUpdateCollectionSchemaCard,
    [EQuillVisibleTool.CreateAsset]: renderCreateAssetCard,
    [EQuillVisibleTool.SetDiscovered]: renderSetDiscoveredCard,
  },
  toolActivities: QUILL_TOOL_ACTIVITIES,
  // Only gates the auto-fulfilled tool. `request_connection` is resolved by its
  // card, so it is deliberately absent — a `true` here would make the hook
  // answer it before the user had picked anything.
  resolvesToolCall: (name, args) =>
    name === EQuillVisibleTool.ShowBrowserSession &&
    readBrowserSessionUrl(args) !== undefined,
  onRemoteToolCall: async call => {
    if (call.name !== EQuillVisibleTool.ShowBrowserSession) {
      // Left to the card that resolves it, or to the server.
      return undefined;
    }
    if (readBrowserSessionUrl(call.args) === undefined) {
      return {
        success: false,
        error: 'show_browser_session requires a non-empty live_view_url.',
      };
    }
    return { success: true, content: BROWSER_SESSION_ACKNOWLEDGEMENT };
  },
});

/** Quill's config with no run view attached (the "view run" card is inert). */
export const QUILL_AGENT_CONFIG: IRainAgentConfig = createQuillAgentConfig({});
